import { useLazyGetDealerDetailsQuery, useUpdateDealerDetailsMutation } from '@api/customer.api';
import { UpdateDealerRequest } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  Button,
  DealerDetailsContacts,
  DealerDetailsUserList,
  DealerShopInformation,
  useDealerDetailsContext,
} from '@components';
import { useAuth, useToast } from '@hooks';
import { Stack } from '@mui/material';
import { t } from '@translations';
import { useLocation } from 'react-router-dom';
import { checkboxFormDataParser } from './DealerDetails.config';

interface DealerFormData extends Omit<UpdateDealerRequest, 'brands' | 'shopTypes'> {
  shopTypes: string[] | string | boolean;
  brands: string[] | string | boolean;
}

type DealerDetailsFormProps = {
  isWorking: boolean;
  dealerId?: string | null;
  contentLinkId?: number;
};

export const DealerDetailsForm = ({
  isWorking,
  dealerId,
  contentLinkId,
}: DealerDetailsFormProps) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const searchParamId = urlParams.get('id');
  const [updateDealer, updateDealerResponse] = useUpdateDealerDetailsMutation();
  const [getDealerDetails, getGetDealerResponse] = useLazyGetDealerDetailsQuery();
  const { canEditData, form, toggleEditDataState, resetForm } = useDealerDetailsContext();
  const { toast } = useToast();
  const { isOEMUser, isServiceUser } = useAuth();
  const isServiceOrOEMUser = isServiceUser || isOEMUser;
  const errorKeyHandler = (key: string) => {
    return key
      .replace(/\[(\d+)\]/g, '.$1')
      .replace(/^./, (str) => str.toLowerCase())
      .split('.')
      .map((part, index) => {
        if (index === 0 || index === 1) return part;
        return part.charAt(0).toLowerCase() + part.slice(1);
      })
      .join('.');
  };

  const isFormLoading = () => {
    return isWorking || updateDealerResponse.isLoading || getGetDealerResponse.isLoading;
  };

  const onSubmit = async (formData: DealerFormData) => {
    const contacts =
      form.getValues('contacts').map((contact) => {
        const formDataContact = formData.contacts.find((item) => {
          return item.contactType === contact.contactType;
        });
        return {
          contactType: contact.contactType,
          contactValue: formDataContact?.contactValue || '',
        };
      }) || [];

    const updatedFormData: UpdateDealerRequest = {
      ...formData,
      contacts,
      shopTypes: checkboxFormDataParser(formData.shopTypes),
      brands: checkboxFormDataParser(formData.brands),
    };

    try {
      await updateDealer(updatedFormData).unwrap();
      await getDealerDetails(dealerId || '').unwrap();
      toast({
        severity: 'success',
        message: t(TextCodes.Dealer.Details.UpdateSuccess),
      });
      resetForm();
      toggleEditDataState(false);
    } catch (error) {
      const errors = (error as any)?.data?.errors;
      Object.keys(errors).forEach((key) => {
        form.setError(errorKeyHandler(key) as keyof UpdateDealerRequest, {
          message: errors[key][0],
        });
        toast({
          severity: 'error',
          message: t(TextCodes.Dealer.Details.UpdateError),
        });
      });
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} id={`dealer-details-form-${contentLinkId}`}>
      {(!isServiceOrOEMUser || searchParamId) && <DealerShopInformation />}
      {(!isServiceOrOEMUser || searchParamId) && <DealerDetailsContacts />}
      <DealerDetailsUserList />
      {canEditData && (
        <Stack direction='row' mt={4} spacing={3} justifyContent='end'>
          <Button
            variant='secondary'
            type='button'
            sx={{ mt: 4, marginInlineStart: 'auto' }}
            onClick={() => toggleEditDataState(false)}
          >
            {t(TextCodes.Base.Cancel)}
          </Button>
          {!isServiceOrOEMUser && (
            <Button
              variant='primary'
              type='submit'
              busy={isFormLoading()}
              sx={{ mt: 4, marginInlineStart: 'auto' }}
              disabled={isFormLoading()}
            >
              {t(TextCodes.Dealer.Details.Save)}
            </Button>
          )}
        </Stack>
      )}
    </form>
  );
};
